import React from 'react';
import { Route } from 'react-router-dom';
import { graphql, compose } from 'react-apollo';

import GetCampaignQuery from 'GraphQueries/Campaigns/GetQuery';
import UpdateCampaignMutation from 'GraphQueries/Campaigns/UpdateMutation';
import AllWebsitesQuery from 'GraphQueries/Websites/AllQuery';
import AllProvidersQuery from 'GraphQueries/Providers/AllQuery';
import NewBudgetLineMutation from 'GraphQueries/BudgetLines/NewMutation';
import UpdateBudgetLineMutation from 'GraphQueries/BudgetLines/UpdateMutation';
import DeleteBudgetLineMutation from 'GraphQueries/BudgetLines/DeleteMutation';
import UpdateBatchBudgetLineMutation from 'GraphQueries/BudgetLines/UpdateBatchMutation';
import DeleteBatchBudgetLineMutation from 'GraphQueries/BudgetLines/DeleteBatchMutation';
import DeleteExternalMapMutation from 'GraphQueries/ExternalMaps/DeleteMutation';

import PageNavigationTabs from 'Components/Presentation/_Shared/Layout/PageNavigationTabs';
import CampaignEdit from 'Components/Presentation/Campaigns/CampaignEdit';
import CampaignMediaPlan from 'Components/Presentation/Campaigns/CampaignMediaPlan';

class CampaignEditContainer extends React.Component {

    static defaultProps = {
        campaign: { name: '', advertiser: { name: '' }, layers: [] },
        reports: [],
        loading: false,
        pageTabs: [
            { label: 'Back To List', path: 'list', includeId: false },
            { label: 'Settings', path: 'manage/settings', includeId: true },
            { label: 'Media Plan', path: 'manage/media-plan', includeId: true }
        ]
    }

    state = { 
        activeTabIndex: 1,
        saving: false
    }

    componentDidMount() {
        const { subscribeToChanges } = this.props;
        subscribeToChanges();
    }

    tabClickHandler = (el, key) => {
        const { history, match: { params } } = this.props;
        history.push(el.includeId ?
            `/media/campaigns/${params.id}/v${params.version}/${el.path}` :
            `/media/campaigns/${el.path}`);
        this.setState({ activeTabIndex: key });
    }

    setActiveTab = index => {
        this.setState({ activeTabIndex: index });
    }

    navigateWithData = (uri) => {
        const { history } = this.props;
        history.push(uri);
    }

    saveCampaign = async (campaign, action = '') => {
        const { onAlert, onUpdate } = this.props;
        this.setState({ saving: true });
        onUpdate(campaign).then(x => {
            let message = '';
            switch (action) {
                case 'add_layer':   
                    message = `Successfully added layer to campaign: ${campaign.id}`;
                    break;
                case 'update_layer':
                    message = `Successfully updated layer on campaign: ${campaign.id}`;
                    break;
                case 'delete_layer':
                    message = `Successfully deleted layer on campaign: ${campaign.id}`;
                    break;
                default:
                    message = `Successfully updated campaign: ${campaign.id}`;
                    break;                    
            }
            this.setState({ saving: false });
            onAlert('success', `${message}`);
        }).catch(error => {
            if(error.graphQLErrors) {
                error.graphQLErrors.map(({ message }, i) => {
                    if (message.match(/not authorized/gi)) {
                        onAlert('error', 'You are not authorised to update campaigns, please see a System Admin');
                    }
                    else { onAlert('error', message); }
                });
            }
            this.setState({ saving: false });
        })
    }

    saveBudgetLine = async (element) => {
        const { onAlert, onAddBudgetLine } = this.props;
        this.setState({ saving: true });

        let externalMaps = Object.assign([], element.externalMaps);
        delete element.externalMaps;
        let budgetLine = Object.assign({}, element);

        onAddBudgetLine(budgetLine, externalMaps).then(x => {
            this.setState({ saving: false });
            onAlert('success', `Successfully created budget line: ${budgetLine.id}`);
        }).catch(error => {
            if(error.graphQLErrors){
                error.graphQLErrors.map(({ message }, i) => {
                    if (message.match(/not authorized/gi)) {
                        onAlert('error', 'You are not authorised to create budget lines, please see a System Admin');
                    }
                    else {
                        onAlert('error', message);
                    }
                });
            }
            this.setState({ saving: false });
        });
    }

    updateBudgetLine = async (element) => {
        const { onAlert, onUpdateBudgetLine } = this.props;
        this.setState({ saving: true });

        let externalMaps = Object.assign([], element.externalMaps);
        delete element.__typename;
        delete element.externalMaps;
        let budgetLine = Object.assign({}, element);

        onUpdateBudgetLine(budgetLine, externalMaps).then(x => {
            this.setState({ saving: false });
            onAlert('success', `Successfully updated budget line: ${budgetLine.id}`);
        }).catch(error => {
            if(error.graphQLErrors) {
                error.graphQLErrors.map(({ message }, i) => {
                    if (message.match(/not authorized/gi)) {
                        onAlert('error', 'You are not authorised to update budget lines, please see a System Admin');
                    }
                    else { onAlert('error', message); }
                });
            }
            this.setState({ saving: false });
        });
    }

    deleteBudgetLine = async (budgetLine) => {
        const { onAlert, onDeleteBudgetLine } = this.props;
        this.setState({ saving: true });
        onDeleteBudgetLine(budgetLine.id, budgetLine.externalMaps).then(x => {
            this.setState({ saving: false });
            onAlert('success', `Successfully deleted budget line: ${budgetLine.id}`);
        }).catch(error => {
            if(error.graphQLErrors) {
                error.graphQLErrors.map(({ message }, i) => {
                    if (message.match(/not authorized/gi)) {
                        onAlert('error', 'You are not authorised to delete budget lines, please see a System Admin');
                    }
                    else { onAlert('error', message); }
                });
            }
            this.setState({ saving: false });
            return this;
        });
    }

    saveBatchBudgetLines = async(budgetLines) => {
        const { onBatchAdd } = this.props;
        this.setState({ saving: true });

        let externalMaps = [];
        budgetLines.forEach(element => {
            externalMaps = externalMaps.concat(element.externalMaps);
        });

        onBatchAdd(budgetLines, externalMaps).then(x => {
            this.setState({ saving: false });
        });
    }

    deleteBatchBudgetLines = async(budgetLines) => {
        const { onBatchDelete } = this.props;
        this.setState({ saving: true });

        let externalMaps = [];
        budgetLines.forEach(element => {
            externalMaps = externalMaps.concat(element.externalMaps);
        });

        onBatchDelete(budgetLines, externalMaps).then(x => { 
            this.setState({ saving: false });
        });
    }

    deleteExternalMap = async(externalMap) => {
        const { onAlert, onDeleteExternalMap } = this.props;
        this.setState({ saving: true });
        onDeleteExternalMap(externalMap.externalId, externalMap.providerId).then(x => {
            this.setState({ saving: false });
            onAlert('success', `Successfully deleted external map`);
        }).catch(error => {
            if(error.graphQLErrors) {
                error.graphQLErrors.map(({ message }, i) => {
                    if (message.match(/not authorized/gi)) {
                        onAlert('error', 'You are not authorised to delete external maps, please see a System Admin');
                    }
                    else { onAlert('error', message); }
                });
            }
            this.setState({ saving: false });
            return this;
        });
    }

    render() {
        const { campaign, loading, websites, websitesLoading, providers, providersLoading, pageTabs } = this.props;
        const { activeTabIndex, saving } = this.state;
        return (
            <React.Fragment>
                <PageNavigationTabs pageTabs={pageTabs} activeTabIndex={activeTabIndex} onTabClick={this.tabClickHandler} />
                <Route path="/media/campaigns/:id/v:version/manage/settings" render={props =>
                    <CampaignEdit campaign={campaign} loading={loading || saving} onCampaignUpdated={this.saveCampaign} 
                        onActivate={event => this.setActiveTab(1)} />} />
                <Route path="/media/campaigns/:id/v:version/manage/media-plan" render={props =>
                    <CampaignMediaPlan campaign={campaign} loading={loading || websitesLoading || providersLoading || saving} 
                        websites={websites} websitesLoading={websitesLoading} 
                        providers={providers} providersLoading={providersLoading} 
                        onCampaignUpdated={this.saveCampaign}
                        onBudgetLineAdded={this.saveBudgetLine} onBudgetLineUpdated={this.updateBudgetLine} onBudgetLineRemoved={this.deleteBudgetLine}                        
                        onBatchBudgetLinesUpdated={this.saveBatchBudgetLines} onBatchBudgetLinesDeleted={this.deleteBatchBudgetLines} 
                        onDeleteExternalMap={this.deleteExternalMap} onActivate={event => this.setActiveTab(2)} />} /> 
            </React.Fragment>
        );
    }
}

export default
    compose(
        graphql(GetCampaignQuery, {
            options: ({ match: { params: { id, version } } }) => ({ variables: { id, version } }),
            props: ({ data: { getCampaign, loading } }) => ({ campaign: getCampaign, loading })
        }),
        graphql(AllWebsitesQuery(['channelId', 'channel { id name }', 'purchaseModelIds', 'purchaseModels { id name }', 'formats']), {
            props: ({ data: { listWebsites, loading } }) => ({ websites: listWebsites && listWebsites.items, websitesLoading: loading })
        }),
        graphql(AllProvidersQuery(['externalFields']), {
            props: ({ data: { listProviders, loading } }) => ({ providers: listProviders && listProviders.items, providersLoading: loading })
        }),
        graphql(UpdateCampaignMutation, {
            options: ({ match: { params: { id, version } } }) => ({ refetchQueries: [{ query: GetCampaignQuery, variables: { id, version } }] }),
            props: ({ mutate }) => ({ onUpdate: campaign => mutate({ variables: { ...campaign, layers: [...campaign.layers.map(x => ({ id: x.id, name: x.name }))] } }) })            
        }),
        graphql(NewBudgetLineMutation(), {
            options: ({ match: { params: { id, version } } }) => ({ refetchQueries: [{ query: GetCampaignQuery, variables: { id, version } }] }),
            props: ({ mutate }) => ({ onAddBudgetLine: (budgetLine, externalMaps) => mutate({ variables: { budgetLine: budgetLine, externalMaps: externalMaps } }) })
        }), 
        graphql(UpdateBudgetLineMutation(), {
            options: ({ match: { params: { id, version } } }) => ({ refetchQueries: [{ query: GetCampaignQuery, variables: { id, version } }] }),
            props: ({ mutate }) => ({ onUpdateBudgetLine: (budgetLine, externalMaps) => mutate({ variables: { 
                budgetLine: { ...budgetLine, flights: budgetLine.flights ? [...budgetLine.flights.map(m => ({ id: m.id, startDate: m.startDate, endDate: m.endDate, budget: m.budget, estimatedUnits: m.estimatedUnits, unitCost: m.unitCost }))] : [] }, 
                externalMaps: [...externalMaps.map(e => ({ externalId: e.externalId, providerId: e.providerId, budgetLineId: e.budgetLineId, externalField: e.externalField, flightId: e.flightId }) )] }
            }) })            
        }),
        graphql(DeleteBudgetLineMutation(), {
            options: ({ match: { params: { id, version } } }) => ({ refetchQueries: [{ query: GetCampaignQuery, variables: { id, version } }] }),
            props: ({ mutate }) => ({ onDeleteBudgetLine: (id, externalMaps) => mutate({ variables: { 
                id,
                externalMaps: [...externalMaps.map(e => ({ externalId: e.externalId, providerId: e.providerId }) )]
            } }) })            
        }),
        graphql(UpdateBatchBudgetLineMutation, {
            options: ({ match: { params: { id, version } } }) => ({ refetchQueries: [{ query: GetCampaignQuery, variables: { id, version } }] }),
            props: ({ mutate }) => ({ onBatchAdd: (budgetLines, externalMaps) => mutate({ variables: { budgetLines: [...budgetLines.map(x => ({ 
                    id: x.id, campaignId: x.campaignId, layerId: x.layerId, websiteId: x.websiteId, purchaseModelId: x.purchaseModelId, 
                    format: x.format, details: x.details, buyType: x.buyType,costModifier: x.costModifier, comments: x.comments, 
                    flights: x.flights ? [...x.flights.map(m => ({ id: m.id, startDate: m.startDate, endDate: m.endDate, budget: m.budget, estimatedUnits: m.estimatedUnits, unitCost: m.unitCost }))] : []
                }))],
                externalMaps: [...externalMaps.map(e => ({ externalId: e.externalId, providerId: e.providerId, budgetLineId: e.budgetLineId, externalField: e.externalField, flightId: e.flightId }) )]
            } }) })
        }),
        graphql(DeleteBatchBudgetLineMutation, {
            options: ({ match: { params: { id, version } } }) => ({ refetchQueries: [{ query: GetCampaignQuery, variables: { id, version } }] }),
            props: ({ mutate }) => ({ onBatchDelete: (budgetLines, externalMaps) => mutate({ variables: { 
                budgetLineIds: [...budgetLines.map(x => (x.id))],
                externalMapIds: [...externalMaps.map(e => ({ externalId: e.externalId, providerId: e.providerId }) )] 
            } }) })            
        }),
        graphql(DeleteExternalMapMutation(), { props: ({ mutate }) => ({ onDeleteExternalMap: (externalId, providerId) => mutate({ variables: { externalId, providerId } }) }) })        
    )(CampaignEditContainer);


// WEBPACK FOOTER //
// ./src/Components/Containers/Campaigns/Edit.js